"use client";

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { parsePositiveWholeNumberInput } from "../utils/positiveWholeNumberInput";
import VotingSettingBox, { getVotingSettingInputClasses } from "./VotingSettingBox";
type ThresholdTimeUnit = "minutes" | "hours";
const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const getUnitMs = (unit: ThresholdTimeUnit): number => unit === "hours" ? HOUR_IN_MS : MINUTE_IN_MS;
const getPreferredUnit = (thresholdTimeMs: number | null): ThresholdTimeUnit => {
  if (thresholdTimeMs !== null && Number.isInteger(thresholdTimeMs) && thresholdTimeMs > 0 && thresholdTimeMs % HOUR_IN_MS === 0) {
    return "hours";
  }
  return "minutes";
};
const getDisplayValue = ({
  thresholdTimeMs,
  unit
}: {
  readonly thresholdTimeMs: number | null;
  readonly unit: ThresholdTimeUnit;
}): string => {
  if (thresholdTimeMs === null || !Number.isFinite(thresholdTimeMs) || thresholdTimeMs <= 0) {
    return "";
  }
  return (thresholdTimeMs / getUnitMs(unit)).toString();
};
export default function CreateWaveVotingThresholdTime(t0) {
  const $ = _c(43);
  const {
    thresholdTimeMs,
    errorMessage,
    setThresholdTimeMs
  } = t0;
  let t1;
  if ($[0] !== thresholdTimeMs) {
    t1 = () => getPreferredUnit(thresholdTimeMs);
    $[0] = thresholdTimeMs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [unit, setUnit] = useState(t1);
  let t2;
  if ($[2] !== thresholdTimeMs) {
    t2 = () => getDisplayValue({
      thresholdTimeMs,
      unit: getPreferredUnit(thresholdTimeMs)
    });
    $[2] = thresholdTimeMs;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [inputValue, setInputValue] = useState(t2);
  const previousThresholdTimeMsRef = useRef(thresholdTimeMs);
  const pendingThresholdTimeMsRef = useRef(undefined);
  const hasError = errorMessage !== undefined;
  const hasThresholdTime = inputValue !== "";
  let t3;
  let t4;
  if ($[4] !== thresholdTimeMs || $[5] !== unit) {
    t3 = () => {
      if (previousThresholdTimeMsRef.current === thresholdTimeMs) {
        return;
      }
      previousThresholdTimeMsRef.current = thresholdTimeMs;
      if (pendingThresholdTimeMsRef.current === thresholdTimeMs) {
        pendingThresholdTimeMsRef.current = undefined;
        return;
      }
      pendingThresholdTimeMsRef.current = undefined;
      setInputValue(getDisplayValue({
        thresholdTimeMs,
        unit
      }));
    };
    t4 = [thresholdTimeMs, unit];
    $[4] = thresholdTimeMs;
    $[5] = unit;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== setThresholdTimeMs || $[9] !== thresholdTimeMs) {
    t5 = (value, nextUnit) => {
      const parsedValue = parsePositiveWholeNumberInput(value);
      const nextThresholdTimeMs = value.trim() === "" ? null : parsedValue !== null ? parsedValue * getUnitMs(nextUnit) : 0;
      pendingThresholdTimeMsRef.current = nextThresholdTimeMs === thresholdTimeMs ? undefined : nextThresholdTimeMs;
      setThresholdTimeMs(nextThresholdTimeMs);
    };
    $[8] = setThresholdTimeMs;
    $[9] = thresholdTimeMs;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const updateThresholdTimeMs = t5;
  let t6;
  if ($[11] !== unit || $[12] !== updateThresholdTimeMs) {
    t6 = e => {
      const nextValue = e.target.value;
      setInputValue(nextValue);
      updateThresholdTimeMs(nextValue, unit);
    };
    $[11] = unit;
    $[12] = updateThresholdTimeMs;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onThresholdTimeChange = t6;
  let t7;
  if ($[14] !== inputValue || $[15] !== updateThresholdTimeMs) {
    t7 = e_0 => {
      const nextUnit_0 = e_0.target.value as ThresholdTimeUnit;
      setUnit(nextUnit_0);
      updateThresholdTimeMs(inputValue, nextUnit_0);
    };
    $[14] = inputValue;
    $[15] = updateThresholdTimeMs;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const onUnitChange = t7;
  const t8 = errorMessage ?? "";
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <>Optional. Leave blank to approve as soon as the threshold is reached. Set a time to require the score to stay above the threshold.</>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== hasError || $[19] !== hasThresholdTime) {
    t10 = getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    });
    $[18] = hasError;
    $[19] = hasThresholdTime;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = hasError ? "approval-threshold-time-error approval-threshold-time-help" : "approval-threshold-time-help";
  let t12;
  if ($[21] !== hasError || $[22] !== inputValue || $[23] !== onThresholdTimeChange || $[24] !== t10 || $[25] !== t11) {
    t12 = <input type="text" inputMode="numeric" autoComplete="off" value={inputValue} onChange={onThresholdTimeChange} id="approval-threshold-time" className={t10} aria-invalid={hasError} aria-describedby={t11} />;
    $[21] = hasError;
    $[22] = inputValue;
    $[23] = onThresholdTimeChange;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== hasError || $[28] !== hasThresholdTime) {
    t13 = getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    });
    $[27] = hasError;
    $[28] = hasThresholdTime;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <option value="minutes">Minutes</option>;
    t15 = <option value="hours">Hours</option>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t14 = $[30];
    t15 = $[31];
  }
  let t16;
  if ($[32] !== onUnitChange || $[33] !== t13 || $[34] !== unit) {
    t16 = <select value={unit} onChange={onUnitChange} aria-label="Minimum time above threshold unit" className={t13}>{t14}{t15}</select>;
    $[32] = onUnitChange;
    $[33] = t13;
    $[34] = unit;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t12 || $[37] !== t16) {
    t17 = <div className="tw-grid tw-gap-3 sm:tw-grid-cols-[minmax(0,1fr)_9rem]">{t12}{t16}</div>;
    $[36] = t12;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== hasError || $[40] !== t17 || $[41] !== t8) {
    t18 = <VotingSettingBox errorId="approval-threshold-time-error" errorMessage={t8} hasError={hasError} helpId="approval-threshold-time-help" helpText={t9} inputId="approval-threshold-time" label="Minimum time above threshold">{t17}</VotingSettingBox>;
    $[39] = hasError;
    $[40] = t17;
    $[41] = t8;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}